import React, { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Theme color configs
const THEME_COLORS = {
  blaze: {
    single: { color: '#FF5500', glow: '#DC143C' },
    multi: { color: '#FFD700', glow: '#FF8C00' },
  },
  frost: {
    single: { color: '#00E5E5', glow: '#00FFFF' },
    multi: { color: '#FFFFFF', glow: '#00FFFF' },
  },
};

// Board skin ID → theme key
const SKIN_TO_THEME = {
  Blaze: 'blaze',
  Cyan: 'frost',
  // Spectrum handled separately via spectrumColor prop
};

const COMBO_LABELS = [null, '1x', '2x', '3x', '4x'];
const DURATION = 1.65; // seconds

const ScoreEffect = ({ rows, score, skinName, spectrumColor, onComplete }) => {
  const meshRef = useRef();
  const startTime = useRef(Date.now());

  const isMulti = rows > 1;
  const comboLabel = COMBO_LABELS[Math.min(rows, 4)];
  const themeKey = SKIN_TO_THEME[skinName];
  const isSpectrum = skinName === 'Spectrum';

  // Determine colors
  const colors = useMemo(() => {
    if (isSpectrum && spectrumColor) {
      return { color: spectrumColor, glow: spectrumColor };
    }
    const theme = THEME_COLORS[themeKey];
    if (theme) {
      return isMulti ? theme.multi : theme.single;
    }
    // Fallback
    return { color: '#FFFFFF', glow: '#AAAAFF' };
  }, [themeKey, isSpectrum, spectrumColor, isMulti]);

  // Create canvas texture with score text
  const texture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, 256, 128);

    // Glow effect
    ctx.shadowColor = colors.glow;
    ctx.shadowBlur = isMulti ? 24 : 12;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const centerX = 128;

    // Draw combo label if multi
    if (comboLabel) {
      ctx.font = 'bold italic 40px "Courier New", monospace';
      ctx.fillStyle = colors.color;
      ctx.fillText(comboLabel, centerX, 38);
    }

    // Draw score value
    ctx.font = 'bold italic 36px "Courier New", monospace';
    ctx.fillStyle = colors.color;
    ctx.shadowBlur = isMulti ? 30 : 16;
    const scoreY = comboLabel ? 82 : 64;
    ctx.fillText(`+${score}`, centerX, scoreY);

    const tex = new THREE.CanvasTexture(canvas);
    tex.needsUpdate = true;
    return tex;
  }, [score, comboLabel, colors, isMulti]);

  // Cleanup texture on unmount
  useEffect(() => {
    return () => {
      texture.dispose();
    };
  }, [texture]);

  useFrame(() => {
    if (!meshRef.current) return;

    const elapsed = (Date.now() - startTime.current) / 1000;
    const t = Math.min(elapsed / DURATION, 1);

    let scale, opacity, yOffset;

    if (t < 0.13) {
      // 0-13%: Scale up from 0.06 to 1.45, move up
      const p = t / 0.13;
      scale = 0.06 + (1.45 - 0.06) * p;
      opacity = p;
      yOffset = -0.5 + p * 2.5;
    } else if (t < 0.25) {
      // 13-25%: Settle to scale 1.0
      const p = (t - 0.13) / 0.12;
      scale = 1.45 + (1.0 - 1.45) * p;
      opacity = 1;
      yOffset = 2.0 + p * 0.5;
    } else if (t < 0.65) {
      // 25-65%: Hold visible
      scale = 1.0;
      opacity = 1;
      yOffset = 2.5;
    } else {
      // 65-100%: Float up, scale down, fade out
      const p = (t - 0.65) / 0.35;
      scale = 1.0 - 0.5 * p;
      opacity = 1 - p;
      yOffset = 2.5 + p * 5;
    }

    meshRef.current.scale.set(scale, scale, 1);
    meshRef.current.position.y = 10 + yOffset; // Center of board (20 rows / 2 = 10)
    meshRef.current.material.opacity = Math.max(0, opacity);

    if (t >= 1) {
      onComplete?.();
    }
  });

  return (
    <mesh ref={meshRef} position={[0, 10, 1.5]}>
      <planeGeometry args={[6, 3]} />
      <meshBasicMaterial
        map={texture}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        opacity={0}
      />
    </mesh>
  );
};

export default ScoreEffect;
